import React, { useState } from "react";
import { Loader2, Brain, Clapperboard, Download } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { renderAssetUrl } from "@/lib/api";
import { PhaseList, StepError } from "./Stepper";

const Start = ({ label, onClick, id, icon: Icon }) => {
  const [busy, setBusy] = useState(false);
  return (
    <button className="btn btn-gold" disabled={busy} data-testid={id}
      onClick={async () => { setBusy(true); try { await onClick(); } finally { setBusy(false); } }}>
      {busy ? <Loader2 size={15} className="animate-spin" /> : <Icon size={15} />}{label}</button>
  );
};

export const StepDeepAI = ({ w, onStart, onRetry, onNext }) => {
  const st = w.steps.deep_ai;
  const p = w.pending_by_type || {};
  const extra = { per_clip: (p.TRANSCRIBE || p.DETECT_FACES) ? `${p.TRANSCRIBE || 0} transcripts · ${p.DETECT_FACES || 0} faces left` : null };
  return (
    <div className="space-y-5" data-testid="wizard-panel-deep_ai">
      <p className="text-[#94A3B8] text-sm max-w-3xl">Vows and speeches get transcribed, faces are found and grouped, and cameras are colour-matched, but only for the clips in your approved edit{w.deep_clips ? ` (${w.deep_clips} clips)` : ""}, not all {w.media?.total} clips.</p>
      {st.status === "pending" && <Start label="Start Deep AI" onClick={onStart} id="btn-wizard-start-deep" icon={Brain} />}
      {st.status !== "pending" && <PhaseList phases={w.phases.deep_ai} step={st} eta={w.eta_seconds} extra={extra} />}
      <StepError step={st} onRetry={onRetry} />
      {st.status === "done" && <div className="flex justify-end"><button className="btn btn-gold" onClick={onNext} data-testid="btn-wizard-to-final"><Clapperboard size={15} />Next: Final Render</button></div>}
    </div>
  );
};

export const StepFinal = ({ pid, w, onStart, onRetry }) => {
  const st = w.steps.final;
  const nav = useNavigate();
  const extra = { render: w.render_job?.status === "running" ? `${w.render_job.progress || 0}%` : null,
    tune: w.render_job?.status === "running" ? `${w.render_job.progress || 0}%` : null };
  return (
    <div className="space-y-5" data-testid="wizard-panel-final">
      <p className="text-[#94A3B8] text-sm max-w-3xl">Renders the 1080p master and highlight reel from the original camera files, with colour matching and the audio mix. After that, auto-tune fixes empty scenes, crossfades and ducking, and re-renders only if something changed.</p>
      {st.status === "pending" && <Start label="Start Final Render" onClick={onStart} id="btn-wizard-start-final" icon={Clapperboard} />}
      {st.status !== "pending" && <PhaseList phases={w.phases.final} step={st} eta={w.eta_seconds} extra={extra} />}
      <StepError step={st} onRetry={onRetry} />
      {w.tune?.changes?.length > 0 && <div className="metric-label" data-testid="wizard-tune-changes">Auto-tune: {w.tune.changes.join("; ")}</div>}
      {st.status === "done" && (
        <div className="grid md:grid-cols-2 gap-4" data-testid="wizard-final-output">
          <div><div className="metric-label mb-1">Master film</div><video src={renderAssetUrl(pid, "master")} controls className="w-full rounded-lg bg-black aspect-video" data-testid="wizard-master-player" /></div>
          <div><div className="metric-label mb-1">Highlight reel</div><video src={renderAssetUrl(pid, "highlight")} controls className="w-full rounded-lg bg-black aspect-video" data-testid="wizard-highlight-player" /></div>
          <div className="md:col-span-2 flex justify-end"><button className="btn btn-gold" onClick={() => nav("/export")} data-testid="btn-wizard-export"><Download size={15} />Export & share</button></div>
        </div>
      )}
    </div>
  );
};
